'use client';

import { useContext, useMemo } from 'react';
import type { AnalyticsClient } from '../../types/analytics-client';
import { peekBrowserAnalyticsCoordinator } from '../browser/coordinator-singleton';
import { AnalyticsCoordinatorContext } from './AnalyticsProvider';

const MISSING_ANALYTICS_CLIENT_ERROR =
  'useAnalyticsClient requires LaunchAnalyticsProvider or a host-registered coordinator.';

function resolveAnalyticsClient(
  clientFromContext: AnalyticsClient | null,
  registered: AnalyticsClient | undefined
): AnalyticsClient {
  if (registered) {
    return registered;
  }
  if (clientFromContext) {
    return clientFromContext;
  }

  throw new Error(MISSING_ANALYTICS_CLIENT_ERROR);
}

/** Resolves a host-registered coordinator first, then the provider client. Does not auto-create. */
export function useAnalyticsClient<T>(createClient: (client: AnalyticsClient) => T): T {
  const clientFromContext = useContext(AnalyticsCoordinatorContext);
  const client = resolveAnalyticsClient(clientFromContext, peekBrowserAnalyticsCoordinator());
  return useMemo(() => createClient(client), [client, createClient]);
}

/** Stable identity mapper so useAnalyticsCoordinator passes a stable createClient reference. */
function returnAnalyticsClient(client: AnalyticsClient): AnalyticsClient {
  return client;
}

export function useAnalyticsCoordinator(): AnalyticsClient {
  return useAnalyticsClient(returnAnalyticsClient);
}
